import { Container, Point } from "pixi.js"
import { findRendererApp } from "../renderer_app"

/**
 * The seam between the HTML loading screen (`#loading` in index.html) and the pixi login screen.
 *
 * The loading screen's small "samsara" does not fade out: the login screen's letters start exactly
 * where the HTML ones are and fly up into the big wordmark. That needs the HTML letters measured in
 * the stage's coordinates, and the loading screen's warm backdrop repeated in pixi so the HTML layer
 * can drop away underneath the letters without the screen changing.
 */

/** One letter of the loading screen's wordmark, in stage coordinates. */
export interface LoadingLetter {
    /** Centre of the letter's advance box. */
    x: number
    baseline: number
    /** Font size in stage units. */
    size: number
}

/** The loading screen's radial backdrop, in stage coordinates. Matches `#loading::before`. */
export interface BackdropEllipse {
    cx: number
    cy: number
    rx: number
    ry: number
}

/** The stops of `#loading::before`'s radial-gradient, which this has to reproduce exactly. */
export const LOADING_BACKDROP_STOPS: [number, string][] = [
    [0, "#2b1e13"],
    [0.38, "#14110e"],
    [0.78, "#07090d"],
]

const loadingElement = (): HTMLElement | null =>
    typeof document === "undefined" ? null : document.getElementById("loading")

const brandElement = (): HTMLElement | null => loadingElement()?.querySelector(".loading-brand") ?? null

/** Is the loading screen up, with its wordmark showing, ready to hand over? */
const loadingIsUp = (): boolean => {
    const loading = loadingElement()
    return !!loading && !loading.hidden && !loading.classList.contains("is-complete")
}

/** Maps a CSS pixel on the page into `stage`'s coordinates (stage.y is stretched by SCALER). */
const pageToStage = (stage: Container): ((x: number, y: number) => Point) | undefined => {
    const app = findRendererApp()
    const view = app?.renderer?.view as HTMLCanvasElement | undefined
    if (!app || !view) {
        return undefined
    }
    const rect = view.getBoundingClientRect()
    if (!rect.width || !rect.height) {
        return undefined
    }
    const screen = app.renderer.screen
    return (x: number, y: number) =>
        stage.toLocal(new Point(((x - rect.left) * screen.width) / rect.width, ((y - rect.top) * screen.height) / rect.height))
}

/**
 * Where each letter of the loading wordmark sits, or undefined when there is nothing to hand over
 * (the loading screen is already gone, or the page cannot be measured -- jsdom, a headless test).
 */
export const measureLoadingWordmark = (stage: Container): LoadingLetter[] | undefined => {
    if (!loadingIsUp()) {
        return undefined
    }
    const brand = brandElement()
    const text = brand?.firstChild
    if (!brand || !text || text.nodeType !== Node.TEXT_NODE || !text.textContent) {
        return undefined
    }
    const toStage = pageToStage(stage)
    if (!toStage) {
        return undefined
    }

    try {
        const style = getComputedStyle(brand)
        const fontSize = parseFloat(style.fontSize)
        const ctx = document.createElement("canvas").getContext("2d")
        if (!ctx || !fontSize) {
            return undefined
        }
        ctx.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`

        // A Range per character gives the left edge of each advance box. The box may or may not
        // include the letter-spacing after it, so the centre comes from the advance alone.
        const range = document.createRange()
        const content = text.textContent
        const centres: number[] = []
        for (let i = 0; i < content.length; i++) {
            range.setStart(text, i)
            range.setEnd(text, i + 1)
            const rect = range.getBoundingClientRect()
            if (!rect.width) {
                return undefined
            }
            centres.push(rect.left + ctx.measureText(content[i]).width / 2)
        }

        // The baseline, exactly: an empty inline-block sits with its bottom edge on it. Added and
        // removed in the same task, so the page never paints it.
        const probe = document.createElement("span")
        probe.style.cssText = "display:inline-block;width:0;height:0;vertical-align:baseline"
        brand.appendChild(probe)
        const baseline = probe.getBoundingClientRect().bottom
        brand.removeChild(probe)
        if (!baseline) {
            return undefined
        }

        const unit = toStage(1, 0).x - toStage(0, 0).x
        return centres.map(cx => {
            const p = toStage(cx, baseline)
            return { x: p.x, baseline: p.y, size: fontSize * unit }
        })
    } catch (e) {
        // getBoundingClientRect on a Range is not implemented everywhere (jsdom). No hand-off then:
        // the login screen falls back to its own entrance.
        return undefined
    }
}

/** The loading screen's backdrop ellipse (CSS `ellipse at 50% 50%`, farthest-corner), in stage units. */
export const measureLoadingBackdrop = (stage: Container): BackdropEllipse | undefined => {
    const loading = loadingElement()
    const toStage = pageToStage(stage)
    if (!loading || !toStage) {
        return undefined
    }
    const r = loading.getBoundingClientRect()
    if (!r.width || !r.height) {
        return undefined
    }
    // farthest-corner keeps closest-side's aspect ratio (w/2 : h/2) and passes through the corner,
    // which makes the radii w/sqrt(2) and h/sqrt(2).
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    const centre = toStage(cx, cy)
    return {
        cx: centre.x,
        cy: centre.y,
        rx: toStage(cx + r.width / Math.SQRT2, cy).x - centre.x,
        ry: toStage(cx, cy + r.height / Math.SQRT2).y - centre.y,
    }
}

/**
 * Start the HTML side of the hand-off: the backdrop drops away (pixi has drawn the same one
 * underneath), "Please wait" and the wheel fade out. The wordmark stays until `hideLoadingWordmark`.
 * False when there was no loading screen to hand over.
 */
export const handOffLoading = (): boolean => {
    const handOff = typeof window !== "undefined" ? (window as any).handOffLoading : undefined
    return typeof handOff === "function" ? !!handOff() : false
}

/** The pixi letters have lifted off: the HTML ones go, on the same frame. */
export const hideLoadingWordmark = () => {
    const brand = brandElement()
    if (brand) {
        brand.style.visibility = "hidden"
    }
}

/** The old ending, for when there is nothing to hand over: the whole loading screen fades. */
export const stopLoading = () => {
    const stop = typeof window !== "undefined" ? (window as any).stopLoading : undefined
    if (typeof stop === "function") {
        stop()
    }
}

export const prefersReducedMotion = (): boolean =>
    typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
